import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard } from 'lucide-react';
import { paymentAccountAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { ConfirmModal, PaymentAccountModal } from '../modals';
import { getAccountTypeIcon, getAccountTypeLabel } from '../../utils/formatters';
import FilterDropdown from '../common/FilterDropdown';
import OwnershipBadge from '../common/OwnershipBadge';

/**
 * PaymentAccounts - Payment accounts management component
 */
function PaymentAccounts({ isManager, isPrivateMode = false, isInHousehold: isInHouseholdProp }) {
  const { success, error: showError } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');

  const canEdit = isManager || isPrivateMode;
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  // Use prop if provided, otherwise fall back to localStorage check
  const isInHousehold = isInHouseholdProp !== undefined ? isInHouseholdProp : !!currentUser.household_id;

  // Get unique account types from current accounts for filter dropdown
  const accountTypes = [...new Set(accounts.map(a => a.account_type).filter(Boolean))]; // eslint-disable-line no-unused-vars

  // Filter accounts
  const filteredAccounts = accounts.filter(account => {
    // Ownership filter
    if (ownershipFilter === 'personal') {
      if (account.household_id) return false;
    } else if (ownershipFilter === 'household') {
      if (!account.household_id) return false;
    }
    
    // Account type filter
    if (accountTypeFilter !== 'all') {
      if (account.account_type !== accountTypeFilter) return false;
    }
    
    return true;
  });

  const loadAccounts = useCallback(async (showLoadingState = true) => {
    if (showLoadingState) setLoading(true);
    try {
      const response = await paymentAccountAPI.getAll(showInactive);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to load payment accounts:', error);
      showError('Failed to load payment accounts');
    } finally {
      if (showLoadingState) setLoading(false);
    }
  }, [showInactive, showError]);

  useEffect(() => {
    loadAccounts(true);
  }, [loadAccounts]);

  const handleAdd = () => {
    setSelectedAccount(null);
    setShowAddModal(true);
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  const handleDelete = (account) => {
    setSelectedAccount(account);
    setShowDeleteModal(true);
  };

  const handleToggleActive = async (account) => {
    try {
      await paymentAccountAPI.toggleActive(account.id, !account.is_active);
      success(`Payment account ${account.is_active ? 'deactivated' : 'activated'}`);
      loadAccounts();
    } catch (error) {
      showError('Failed to update account status');
    }
  };

  const confirmDelete = async () => {
    if (!selectedAccount) return;

    try {
      await paymentAccountAPI.delete(selectedAccount.id);
      success('Payment account deleted successfully');
      setShowDeleteModal(false);
      setSelectedAccount(null);
      loadAccounts();
    } catch (error) {
      showError('Failed to delete payment account');
    }
  };

  // Filter config
  const filterConfig = [
    ...(isInHousehold && !isPrivateMode ? [{
      id: 'ownership',
      label: 'Ownership',
      type: 'select',
      value: ownershipFilter,
      onChange: setOwnershipFilter,
      options: [
        { value: 'all', label: 'All Accounts' },
        { value: 'personal', label: 'Personal' },
        { value: 'household', label: 'Household' }
      ]
    }] : []),
    {
      id: 'accountType',
      label: 'Account Type',
      type: 'select',
      value: accountTypeFilter,
      onChange: setAccountTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'checking', label: 'Checking' },
        { value: 'savings', label: 'Savings' },
        { value: 'credit', label: 'Credit Card' },
        { value: 'debit', label: 'Debit Card' },
        { value: 'other', label: 'Other' }
      ]
    },
    ...(canEdit ? [{
      id: 'showInactive',
      label: 'Show Inactive',
      type: 'toggle',
      value: showInactive,
      onChange: setShowInactive
    }] : [])
  ];

  const activeFilterCount = [
    ownershipFilter !== 'all' ? 1 : 0,
    accountTypeFilter !== 'all' ? 1 : 0,
    showInactive ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className="household-section">
        <h2>Payment Accounts</h2>
        <div className="loading-state">Loading payment accounts...</div>
      </div>
    );
  }

  return (
    <div className="household-section">
      <div className="section-header">
        <div>
          <h2>Payment Accounts</h2>
          <p className="section-description">
            {isPrivateMode 
              ? 'Manage your personal payment accounts and methods'
              : canEdit 
                ? 'Manage household payment accounts and methods' 
                : 'View household payment accounts'}
          </p>
        </div>
        {canEdit && (
          <div className="header-actions">
            <button className="btn btn-primary btn-icon-mobile" onClick={handleAdd} title="Add Account">
              <CreditCard size={18} />
              <span className="btn-text">Add Account</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      {filterConfig.length > 0 && (
        <div className="filters-bar">
          <FilterDropdown
            filters={filterConfig}
            activeCount={activeFilterCount}
          />
        </div>
      )}

      {filteredAccounts.length === 0 ? (
        <div className="empty-state">
          <CreditCard size={48} />
          <h3>No Payment Accounts</h3>
          <p>{accounts.length === 0 ? 'Add your first payment account to get started' : 'No accounts match the selected filter'}</p>
        </div>
      ) : (
        <div className="accounts-grid">
          {filteredAccounts.map(account => (
            <div key={account.id} className={`account-card ${!account.is_active ? 'inactive' : ''}`}>
              <div className="account-card-header">
                <div className="account-icon">{getAccountTypeIcon(account.account_type)}</div>
                <div className="account-info">
                  <h3>{account.account_name}</h3>
                  <span className="account-type">{getAccountTypeLabel(account.account_type)}</span>
                </div>
                {!account.is_active && <span className="inactive-badge">Inactive</span>}
                {isInHousehold && !isPrivateMode && (
                  <OwnershipBadge 
                    isHousehold={!!account.household_id} 
                    variant="icon-only" 
                    size="sm" 
                  />
                )}
              </div>
              
              <div className="account-details">
                {account.card_image && (
                  <div className="account-card-image-compact">
                    <img src={account.card_image} alt="Card preview" />
                  </div>
                )}
                
                <div className="account-info-grid">
                  {account.institution_name && (
                    <div className="detail-row">
                      <span className="detail-label">Institution</span>
                      <span className="detail-value">{account.institution_name}</span>
                    </div>
                  )}
                  {account.last_four && (
                    <div className="detail-row">
                      <span className="detail-label">Last 4</span>
                      <span className="detail-value">•••• {account.last_four}</span>
                    </div>
                  )}
                  {account.notes && (
                    <div className="detail-row">
                      <span className="detail-label">Notes</span>
                      <span className="detail-value notes">{account.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {canEdit && (
                <div className="account-actions">
                  <button className="btn-icon" onClick={() => handleEdit(account)}>Edit</button>
                  <button className="btn-icon" onClick={() => handleToggleActive(account)}>
                    {account.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className="btn-icon danger" onClick={() => handleDelete(account)}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <PaymentAccountModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={async (data) => {
          await paymentAccountAPI.create(data);
          success('Payment account added successfully');
          setShowAddModal(false);
          loadAccounts();
        }}
      />

      <PaymentAccountModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedAccount(null); }}
        account={selectedAccount}
        onSubmit={async (data) => {
          await paymentAccountAPI.update(selectedAccount.id, data);
          success('Payment account updated successfully');
          setShowEditModal(false);
          setSelectedAccount(null);
          loadAccounts();
        }}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedAccount(null); }}
        onConfirm={confirmDelete}
        title="Delete Payment Account"
        message={`Are you sure you want to delete "${selectedAccount?.account_name}"? This action cannot be undone.`}
        confirmText="Delete Account"
        variant="danger"
      />
    </div>
  );
}

export default PaymentAccounts;
